/**
 * Shared quantity formatting for inventory surfaces.
 *
 * Quantities are stored at NUMERIC(14,5); rendering the raw float ("205.05669
 * each", "-0.99996 each") reads as a broken system even when the number is
 * technically true. fmtQty trims to at most 5 dp with no float tails;
 * isNearIntegerArtifact flags the "almost exactly a whole number" residue a
 * legacy rounded recipe ratio leaves behind, so the UI can explain it instead
 * of alarming with it.
 */

export function fmtQty(n: number): string {
  const rounded = Math.round(n * 1e5) / 1e5
  if (Number.isInteger(rounded)) return String(rounded)
  // Up to 5 dp, trailing zeros trimmed.
  return rounded.toFixed(5).replace(/0+$/, '').replace(/\.$/, '')
}

export function fmtQtyUnit(n: number, unit: string): string {
  return `${fmtQty(n)} ${unit}`
}

/**
 * True when a quantity is within a hair of a whole number without being one —
 * the signature of a stored rounded reciprocal (0.08333 × 12 = 0.99996).
 * Counting the item snaps it to reality; re-saving the recipe fixes the cause.
 */
export function isNearIntegerArtifact(n: number): boolean {
  const nearest = Math.round(n)
  const diff = Math.abs(n - nearest)
  return diff > 1e-9 && diff < 0.001
}

export const QTY_ARTIFACT_HINT =
  'Slightly off a whole number because of an older recipe conversion. Counting this item snaps the stock to what is really on the shelf.'
